import Experience from "../Experience.js";
import { Vector3 } from "three";
import { gsap } from "gsap";

export default class UI {
    constructor() {
        this.experience = new Experience()
        this.params = this.experience.config.camera
        this.info = document.querySelector('.info')
        this.overlay = document.querySelector('.overlay');
        this.scrollIndicator = document.querySelector('.scroll-indicator');
        this.scrollHint = document.querySelector('.scroll-hint');
        this.isMobile = this.experience.isMobile;
        this.camera = this.experience.camera
        this.compass = document.querySelector('.compass-container');
        this.needle = document.querySelector('.compass-needle');
        this.timelineBar = document.querySelector('.timeline-bar');
        this.timelineBarFill = document.querySelector('.timeline-bar__fill');
        this.timelineBarMarkers = document.querySelector('.timeline-bar__markers');
        this.holesConfig = this.experience.config.holes;
        this.cameraDirection = new Vector3();

        this.scrollUiActive = this.camera.controlMode === 'scroll'
        this.introOverlayHidden = false
        this.setTimelineBar()
        this.setCompass()
        this.scrollHintDelay = 1000
        this.scrollHintTimer = null
        this.scrollHintEligible = false
        this.hasEngagedScroll = false
        this.setScrollUiActive(this.scrollUiActive)
        this.setScrollHintListeners()
        this.setScrollCueCopy()

    }

    setCompass() {
        if (!this.compass) return

        // Retour au départ du trou 1 (l'écran d'accueil n'est plus
        // accessible en scroll une fois le survol terminé)
        this.onCompassActivate = () => {
            const timeline = this.experience.timeline
            if (!this.scrollUiActive || timeline?.introState !== 'done') return
            this.scrollToTimelineProgress(timeline.getHoleEntryProgress(timeline.firstHole))
        }

        this.compass.addEventListener('click', this.onCompassActivate)
        this.compass.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.preventDefault()
            this.onCompassActivate()
        })
    }

    applyTemplate(template, data) {
        const tpl = Array.isArray(template) ? template.join('') : template;
        return tpl.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => data[key] ?? '');
    }

    addHighlight(data, template) {
        const tplString = Array.isArray(template) ? template.join('') : template;
        this.info.innerHTML = this.applyTemplate(tplString, data);
        this.info.style.opacity = 1
    }

    clearHighlight() {
        this.info.style.opacity = 0
    }

    animateCompass() {
        this.camera.instance.getWorldDirection(this.cameraDirection);

        let angle = Math.atan2(this.cameraDirection.x, this.cameraDirection.z);
        if (angle < 0) angle += 2 * Math.PI;

        this.needle.style.transform = `rotate(${angle}rad)`;
    }


    // L'écran d'accueil reste affiché jusqu'au clic sur « Démarrer le
    // parcours » (ou un lien direct vers un trou) ; il ne revient plus
    // ensuite : la fin de visite a son propre panneau (Finale).
    animateOverlay() {
        if (!this.overlay) return;

        const opacity = this.introOverlayHidden ? 0 : 1;
        this.overlay.style.opacity = opacity;
        this.overlay.style.pointerEvents = opacity > 0.9 ? 'auto' : 'none';
    }

    hideIntroOverlay({ immediate = false } = {}) {
        this.introOverlayHidden = true
        this.overlay?.classList.toggle('is-leaving', !immediate)
        this.animateOverlay()

        // Une fois effacé, l'écran d'accueil est retiré du rendu : sinon,
        // bien qu'invisible, son contenu intercepte les clics au centre
        // de l'écran (.ui-layer * réactive les pointer-events).
        window.clearTimeout(this.overlayHideTimer)
        this.overlayHideTimer = window.setTimeout(() => {
            if (this.overlay && this.introOverlayHidden) this.overlay.style.visibility = 'hidden'
        }, immediate ? 0 : 1400)
    }

    setScrollUiActive(active) {
        this.scrollUiActive = active
        document.body.classList.toggle('orbit-mode', !active)
        if (!active) this.clearScrollHintTimer()
    }

    setScrollCueCopy() {
        const action = this.isMobile
            ? 'Glissez pour explorer le parcours'
            : 'Faire défiler pour commencer'

        document.querySelectorAll('.scroll-indicator .scroll-cue__action').forEach((el) => {
            el.textContent = action
        })
    }

    setScrollHintListeners() {
        this.onScrollHintActivity = () => this.onScrollActivity()

        window.addEventListener('wheel', this.onScrollHintActivity, { passive: true })
        window.addEventListener('touchstart', this.onScrollHintActivity, { passive: true })
        window.addEventListener('touchmove', this.onScrollHintActivity, { passive: true })
        window.addEventListener('keydown', this.onScrollHintActivity)
    }

    removeScrollHintListeners() {
        if (!this.onScrollHintActivity) return

        window.removeEventListener('wheel', this.onScrollHintActivity)
        window.removeEventListener('touchstart', this.onScrollHintActivity)
        window.removeEventListener('touchmove', this.onScrollHintActivity)
        window.removeEventListener('keydown', this.onScrollHintActivity)
        this.onScrollHintActivity = null
    }

    onScrollActivity() {
        this.markScrollEngaged()
        this.hideScrollHint()
        this.scheduleScrollHint()
    }

    markScrollEngaged() {
        if (this.hasEngagedScroll) return
        this.hasEngagedScroll = true
        this.scrollHint?.classList.remove('is-prompting')
        this.scrollIndicator?.classList.remove('is-prompting')
    }

    scheduleScrollHint() {
        this.clearScrollHintTimer()
        if (!this.scrollUiActive || !this.scrollHintEligible) return

        if (!this.hasEngagedScroll) {
            this.showScrollHint()
            return
        }

        this.scrollHintTimer = window.setTimeout(() => {
            this.showScrollHint()
        }, this.scrollHintDelay)
    }

    clearScrollHintTimer() {
        if (this.scrollHintTimer !== null) {
            window.clearTimeout(this.scrollHintTimer)
            this.scrollHintTimer = null
        }
    }

    showScrollHint() {
        if (!this.scrollHint || !this.scrollHintEligible) return
        this.scrollHint.classList.toggle('is-prompting', !this.hasEngagedScroll)
        this.scrollHint.classList.add('is-visible')
        this.scrollHint.setAttribute('aria-hidden', 'false')
    }

    hideScrollHint() {
        if (!this.scrollHint) return
        this.scrollHint.classList.remove('is-visible')
        this.scrollHint.setAttribute('aria-hidden', 'true')
    }

    updateScrollHintEligibility(progress) {
        const overlayHidden = progress > 0.03 && progress < 0.97
        const overlayVisible = progress <= 0.03

        this.scrollIndicator?.classList.toggle(
            'is-prompting',
            overlayVisible && !this.hasEngagedScroll
        )

        if (overlayHidden === this.scrollHintEligible) {
            if (overlayHidden && !this.hasEngagedScroll) {
                this.showScrollHint()
            }
            return
        }

        this.scrollHintEligible = overlayHidden

        if (overlayHidden) {
            this.scheduleScrollHint()
        } else {
            this.hideScrollHint()
            this.clearScrollHintTimer()
        }
    }


    setTimelineBar() {
        if (!this.timelineBar || !this.timelineBarMarkers) return

        const keys = Object.keys(this.holesConfig)
            .map(Number)
            .sort((a, b) => a - b)

        this.timelineBarMarkers.innerHTML = ''

        keys.forEach((hole) => {
            const [min, max] = this.holesConfig[hole]
            const position = ((min + max) / 2) * 100

            const marker = document.createElement('button')
            marker.type = 'button'
            marker.className = 'timeline-bar__marker'
            marker.style.setProperty('--marker-pos', `${position}%`)
            marker.dataset.hole = String(hole)
            marker.dataset.progress = String((min + max) / 2)
            marker.setAttribute('aria-label', `Trou ${hole}`)

            marker.addEventListener('click', () => {
                this.scrollToTimelineProgress(Number(marker.dataset.progress))
            })

            this.timelineBarMarkers.appendChild(marker)
        })
    }

    scrollToTimelineProgress(progress) {
        const timeline = this.experience.timeline
        const lenis = timeline?.lenis
        if (!lenis) return

        const clamped = Math.max(0, Math.min(1, progress))
        timeline.targetProgress = clamped
        lenis.scrollTo(clamped * lenis.limit, { duration: 1.8 })
        this.onScrollActivity?.()
    }

    getActiveHole(progress) {
        const holes = this.holesConfig
        const keys = Object.keys(holes).map(Number).sort((a, b) => a - b)
        const [firstMin] = holes[keys[0]]

        if (progress < firstMin) return null

        for (let i = 0; i < keys.length; i++) {
            const k = keys[i]
            const nextKey = keys[i + 1]
            const [min, max] = holes[k]

            if (!nextKey) {
                if (progress >= min && progress <= max) return k
                break
            }

            const [nMin] = holes[nextKey]
            const transitionStart = nMin
            const transitionEnd = max

            if (progress >= min && progress < transitionStart) return k

            if (progress >= transitionStart && progress <= transitionEnd) return k
        }

        return null
    }

    updateTimelineBar(progress) {
        if (!this.timelineBar || !this.timelineBarFill) return

        const visible = this.scrollUiActive && progress > 0.03 && progress < 0.97
        this.timelineBar.classList.toggle('is-visible', visible)
        this.timelineBar.setAttribute('aria-hidden', String(!visible))

        if (!visible) return

        this.timelineBarFill.style.height = `${progress * 100}%`

        const activeHole = this.getActiveHole(progress)
        const markers = this.timelineBarMarkers?.querySelectorAll('.timeline-bar__marker')
        if (!markers) return

        markers.forEach((marker) => {
            const markerProgress = Number(marker.dataset.progress)
            const hole = Number(marker.dataset.hole)
            const isPassed = progress >= markerProgress
            const isActive = activeHole === hole

            marker.classList.toggle('is-passed', isPassed && !isActive)
            marker.classList.toggle('is-active', isActive)
        })
    }

    animateScrollIndicator(progress) {
        if (!this.scrollIndicator) return

        if (progress > 0.008) {
            this.markScrollEngaged()
        }

        const fadeStart = 0
        const fadeEnd = 0.03

        let opacity = 1

        if (progress <= fadeStart) {
            opacity = 1
        }
        else if (progress >= fadeEnd) {
            opacity = 0
        }
        else {
            const t = (progress - fadeStart) / (fadeEnd - fadeStart)
            const eased = t * t * (3 - 2 * t)
            opacity = 1 - eased
        }

        this.scrollIndicator.style.opacity = opacity
    }

    update(progress) {
        this.animateOverlay();
        this.animateCompass()
        this.animateScrollIndicator(progress);
        this.updateScrollHintEligibility(progress);
        this.updateTimelineBar(progress);
    }

    destroy() {
        this.clearScrollHintTimer()
        this.removeScrollHintListeners()

        if (this.onCompassActivate) {
            this.compass?.removeEventListener('click', this.onCompassActivate)
            this.onCompassActivate = null
        }

        if (this.overlay) {
            gsap.killTweensOf(this.overlay);
        }

        if (this.debug?.active && this.debugFolder) {
            this.debugFolder.dispose();
            this.debugFolder = null;
        }

        this.compass = null;
        this.overlay = null;
        this.timelineBarMarkers = null;
        this.timelineBarFill = null;
        this.timelineBar = null;
        this.params = null;
        this.experience = null;
    }

}
